"use client";
// РУКА 2. Корзина: строки с ценой за единицу, количеством и суммой; комплект — одной строкой;
// промокод; итог. Что лежит в корзине — из браузера (cart.ts), цены — актуальные у Руки 3.
// Тексты — из макета. Оформление, доставка и оплата — неделя 3.
// Вид — из макета (Р-021): строки .cl, справа закреплённый блок «Как получить» и итог. Логика не менялась.
// «Как получить» пока макет — приходит со страницы (ship): серый блок умеет рисовать только сервер.
import Link from "next/link";
import { useEffect, useState, useSyncExternalStore, type ReactNode } from "react";
import type { KitView, ProductView } from "@/contract";
import { setQty, useCart, type CartLine } from "./cart";
import { checkPromo, loadCart } from "./cartData";
import { kitPrices, salePrice } from "./pricing";
import { formatRub } from "./money";
import { PhotoSlot } from "./PhotoSlot";
import { glueUnits } from "./text";

type Data = { products: Record<string, ProductView | null>; kits: Record<string, KitView | null> };
type Promo = { code: string; ok: boolean; discount: number; reason?: string };

const noop = () => () => {};
const keyOf = (l: CartLine) => (l.kind === "product" ? `p:${l.id}` : `k:${l.slug}`);

function Qty({ line }: { line: CartLine }) {
  return (
    <div className="step qty">
      <button type="button" aria-label="Меньше" onClick={() => setQty(line, line.qty - 1)}>
        −
      </button>
      <span>{line.qty}</span>
      <button type="button" aria-label="Больше" onClick={() => setQty(line, line.qty + 1)}>
        +
      </button>
    </div>
  );
}

export function CartView({ proHint, ship, promoNote }: { proHint: string; ship?: ReactNode; promoNote?: ReactNode }) {
  const lines = useCart();
  const mounted = useSyncExternalStore(noop, () => true, () => false);
  const [data, setData] = useState<Data | null>(null);
  const [code, setCode] = useState("");
  const [promo, setPromo] = useState<Promo | null>(null);

  // Состав корзины поменялся (не количество) — подгружаем актуальные товары и комплекты.
  const key = lines.map(keyOf).sort().join(",");
  useEffect(() => {
    if (!key) return;
    let alive = true;
    const keys = key.split(",");
    loadCart(
      keys.filter((k) => k.startsWith("p:")).map((k) => k.slice(2)),
      keys.filter((k) => k.startsWith("k:")).map((k) => k.slice(2)),
    ).then((d) => alive && setData(d));
    return () => {
      alive = false;
    };
  }, [key]);

  const rows = lines.map((l) => {
    if (l.kind === "product") {
      const p = data?.products[l.id];
      return { line: l, p, unit: p ? salePrice(p) : 0 };
    }
    const k = data?.kits[l.slug];
    return { line: l, k, unit: k ? kitPrices(k).together : 0 };
  });
  const subtotal = rows.reduce((s, r) => s + r.unit * r.line.qty, 0);

  // Применённый промокод пересчитываем, когда меняется сумма.
  const applied = promo?.ok ? promo.code : "";
  useEffect(() => {
    if (!applied || !subtotal) return;
    let alive = true;
    checkPromo(applied, subtotal).then((r) => alive && setPromo({ code: applied, ...r }));
    return () => {
      alive = false;
    };
  }, [applied, subtotal]);

  if (!mounted || (lines.length > 0 && !data)) return <p className="sm muted">Загружаем корзину…</p>;

  if (lines.length === 0) {
    return (
      <>
        <h1 className="h1">Корзина</h1>
        <div className="card" style={{ textAlign: "center", padding: "34px 18px" }}>
          <b style={{ fontSize: 19 }}>В корзине пока пусто</b>
          <p className="sm muted" style={{ margin: "8px 0 14px" }}>
            Загляните в каталог или соберите комплект помощником
          </p>
          <div className="row" style={{ justifyContent: "center" }}>
            <Link className="b" href="/catalog">
              В каталог
            </Link>
            <Link className="b gh" href="/podbor">
              Не знаю, что нужно
            </Link>
          </div>
        </div>
      </>
    );
  }

  const discount = promo?.ok ? promo.discount : 0;
  const apply = async () => {
    const c = code.trim();
    if (!c) return;
    setPromo({ code: c.toUpperCase(), ...(await checkPromo(c, subtotal)) });
  };

  return (
    <div className="two">
      <div>
        <div className="sb" style={{ marginBottom: 12 }}>
          <h1 className="h1" style={{ margin: 0 }}>
            Корзина · {lines.reduce((n, l) => n + l.qty, 0)} шт
          </h1>
          <Link className="b gh sm" href="/catalog">
            ← Продолжить покупки
          </Link>
        </div>
        {rows.map((r) =>
          r.line.kind === "product" ? (
            r.p ? (
              <div key={keyOf(r.line)} className="cl">
                <Link href={`/product/${r.p.id}`} className="ph" aria-label={r.p.name}>
                  <PhotoSlot src={r.p.photos[0]} alt={r.p.name} categoryId={r.p.categoryId} />
                </Link>
                <div className="ci">
                  <Link className="cn" href={`/product/${r.p.id}`}>
                    {glueUnits(r.p.name)}
                  </Link>
                  <div className="xs">
                    <Stock p={r.p} />
                    {" · "}
                    <span style={{ whiteSpace: "nowrap" }}>арт. {r.p.code}</span> ·{" "}
                    <span style={{ whiteSpace: "nowrap" }}>
                      <span className="mono">{formatRub(r.unit)}</span> за&nbsp;{r.p.unit}
                    </span>
                  </div>
                  <div className="cla">
                    <button type="button" className="lnk bad" onClick={() => setQty(r.line, 0)}>
                      Удалить
                    </button>
                  </div>
                </div>
                <Qty line={r.line} />
                <div className="ct">
                  <b className="mono">{formatRub(r.unit * r.line.qty)}</b>
                  <div className="xs mono">
                    {formatRub(r.unit)} × {r.line.qty}
                  </div>
                </div>
              </div>
            ) : (
              <div key={keyOf(r.line)} className="cl gone">
                <Gone line={r.line} />
              </div>
            )
          ) : r.k ? (
            <div key={keyOf(r.line)} className="cl kit">
              <div className="ph kitico" aria-hidden="true">
                ⚙
              </div>
              <div className="ci">
                <b className="cn">{r.k.title}</b>
                <div className="xs">
                  {r.k.items.map(({ product: p, qty }, n) => (
                    <span key={p.id}>
                      {n ? " + " : ""}
                      <Link href={`/product/${p.id}`}>{glueUnits(p.name)}</Link>
                      {qty > 1 ? ` × ${qty}` : ""}
                    </span>
                  ))}
                </div>
                <div className="xs" style={{ marginTop: 4 }}>
                  <span className="mono">{formatRub(r.unit)}</span> за комплект
                  {kitPrices(r.k).benefit > 0 ? <span style={{ color: "var(--ok)" }}> · выгода {formatRub(kitPrices(r.k).benefit)}</span> : null}
                </div>
                <div className="cla">
                  <button type="button" className="lnk bad" onClick={() => setQty(r.line, 0)}>
                    Удалить
                  </button>
                </div>
              </div>
              <Qty line={r.line} />
              <div className="ct">
                <b className="mono">{formatRub(r.unit * r.line.qty)}</b>
                <div className="xs mono">
                  {formatRub(r.unit)} × {r.line.qty}
                </div>
              </div>
            </div>
          ) : (
            <div key={keyOf(r.line)} className="cl gone">
              <Gone line={r.line} />
            </div>
          ),
        )}
      </div>

      <div className="sticky">
        <div className="card">
          <form
            className="row"
            style={{ flexWrap: "nowrap" }}
            onSubmit={(e) => {
              e.preventDefault();
              void apply();
            }}
          >
            <input type="text" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Промокод" aria-label="Промокод" />
            <button type="submit" className="b gh">
              Применить
            </button>
          </form>
          {promo && !promo.ok ? <div className="ferr">{promo.reason}</div> : null}
          {promo?.ok ? (
            <div className="xs" style={{ marginTop: 4, color: "var(--ok)" }}>
              Промокод применён: {promo.code}
            </div>
          ) : null}
          {promoNote}

          <div className="stk" style={{ marginTop: 12 }}>
            <div>
              <span className="muted">Товары</span>
              <span className="mono">{formatRub(subtotal)}</span>
            </div>
            {discount > 0 ? (
              <div style={{ color: "var(--ok)" }}>
                <span>Промокод {promo?.code}</span>
                <span className="mono">−{formatRub(discount)}</span>
              </div>
            ) : null}
            <div>
              <b>Итого</b>
              <b className="mono" style={{ fontSize: 22 }}>
                {formatRub(subtotal - discount)}
              </b>
            </div>
          </div>
          {/* как в макете: сначала итог, потом «Как получить» и «Оформить заказ» */}
          {ship}
          <p className="xs" style={{ marginTop: 10, color: "var(--aq-d)" }}>
            {proHint}
          </p>
        </div>
      </div>
    </div>
  );
}

// Наличие в строке — как в макете. Как показывать товар без наличия и что, если берут больше, чем есть, — вопрос В-12.
function Stock({ p }: { p: ProductView }) {
  return p.inStock ? <span style={{ color: "var(--ok)" }}>✓ в наличии</span> : <span style={{ color: "var(--bad)" }}>нет в наличии</span>;
}

// Товар или комплект пропал с сайта (скрыт магазином или убран из 1С) — показываем и даём убрать.
function Gone({ line }: { line: CartLine }) {
  return (
    <div className="sb" style={{ gridColumn: "1 / -1" }}>
      <span className="sm muted">Этой позиции больше нет на сайте</span>
      <button type="button" className="lnk" onClick={() => setQty(line, 0)}>
        Убрать из корзины
      </button>
    </div>
  );
}
